import React from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { RefreshCw, X, Database } from 'lucide-react';
import { format } from 'date-fns';

interface PreviewDataSettingsProps {
    isOpen: boolean;
    onClose: () => void;
    onRegenerate: (settings: { startDate: Date; endDate: Date; currency: string }) => void;
    currentSettings: { startDate: Date; endDate: Date; currency: string };
}

export const PreviewDataSettings: React.FC<PreviewDataSettingsProps> = ({
    isOpen,
    onClose,
    onRegenerate,
    currentSettings
}) => {
    const [startDate, setStartDate] = React.useState<string>(format(currentSettings.startDate, 'yyyy-MM-dd'));
    const [endDate, setEndDate] = React.useState<string>(format(currentSettings.endDate, 'yyyy-MM-dd'));
    const [currency, setCurrency] = React.useState(currentSettings.currency);

    const handleRegenerate = () => {
        onRegenerate({
            startDate: new Date(startDate),
            endDate: new Date(endDate),
            currency
        });
        onClose();
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                <div className="flex justify-between items-start mb-4">
                                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-neutral-900 flex items-center gap-2">
                                        <Database size={18} className="text-blue-500" />
                                        Preview Data Settings
                                    </Dialog.Title>
                                    <button
                                        onClick={onClose}
                                        className="text-neutral-400 hover:text-neutral-600 transition-colors"
                                    >
                                        <X size={20} />
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                                        These settings control the random data used to preview your charts. The data exists only in this session and is not saved to the database.
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-medium text-neutral-600 mb-1">
                                                Start Date
                                            </label>
                                            <input
                                                type="date"
                                                value={startDate}
                                                onChange={(e) => setStartDate(e.target.value)}
                                                className="w-full text-sm border border-neutral-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-neutral-600 mb-1">
                                                End Date
                                            </label>
                                            <input
                                                type="date"
                                                value={endDate}
                                                onChange={(e) => setEndDate(e.target.value)}
                                                className="w-full text-sm border border-neutral-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-medium text-neutral-600 mb-1">
                                            Currency
                                        </label>
                                        <select
                                            value={currency}
                                            onChange={(e) => setCurrency(e.target.value)}
                                            className="w-full text-sm border border-neutral-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                        >
                                            <option value="USD">USD ($)</option>
                                            <option value="EUR">EUR (€)</option>
                                            <option value="GBP">GBP (£)</option>
                                            <option value="JPY">JPY (¥)</option>
                                            <option value="CAD">CAD ($)</option>
                                            <option value="TND">TND (DT)</option>
                                        </select>
                                    </div>

                                    <div className="pt-2">
                                        <button
                                            onClick={handleRegenerate}
                                            className="w-full flex items-center justify-center gap-2 bg-neutral-900 hover:bg-neutral-800 text-white py-2.5 rounded-lg text-sm font-medium transition-colors"
                                        >
                                            <RefreshCw size={16} />
                                            Regenerate Data
                                        </button>
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
